import {useTranslation} from 'react-i18next';
import {Box, Divider, Grid, Skeleton, Stack, Typography} from '@mui/material';
import React, {useState} from 'react';
import {useSnackbar} from 'notistack';
import {finalize} from 'rxjs';

import {ManageExecution} from '@store/operations/executions';
import {DocumentKind, filesService} from '@store/files';

import SelectFileComponent from '@components/inputs/selectFile/SelectFile.component';

import {File} from '@icons/index';
import FileCard from '@components/cards/file/File.card';

interface DocumentsFormProps {
  execution: ManageExecution;
  readOnly?: boolean;
  updateExecution: (data: ManageExecution) => void;
}

const DocumentsForm = (props: DocumentsFormProps) => {
  const {execution, readOnly, updateExecution} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [addingDocument, setAddingDocument] = useState(false);

  const addDocument = (file: File) => {
    setAddingDocument(true);
    filesService.uploadFile(file, DocumentKind.EXECUTION).pipe(finalize(() => setAddingDocument(false))).subscribe({
      next: (document) => {
        if (execution.isNew) {
          updateExecution({documents: (execution.documents || []).concat(document)});
        } else {
          updateExecution({documentsToAdd: (execution.documentsToAdd || []).concat(document)});
        }
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const documents = [...(execution.documents || []), ...(execution.documentsToAdd || [])];

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={16} fontWeight="700">
            {t('operations.executions.manage.documents')}
          </Typography>
          {!readOnly && <SelectFileComponent handleFileSelected={addDocument}/>}
        </Stack>
        <Box>
          <Grid container spacing={2}>
            {documents.map((document) => (
              <Grid item key={document.id}>
                <FileCard
                  document={document}
                  handleDeleteDocument={!readOnly ? () => {
                    if (execution.isNew) {
                      updateExecution({documents: (execution.documents || []).filter((d) => d.id !== document.id)});
                    } else {
                      updateExecution({
                        documents: (execution.documents || []).filter((d) => d.id !== document.id),
                        documentsToDelete: (execution.documentsToDelete || []).concat(document.id),
                      });
                    }
                  } : undefined}/>
              </Grid>
            ))}
            {addingDocument && (
              <Grid item>
                <Skeleton variant="rectangular" height="74px" width="74px" sx={{borderRadius: '4px'}}/>
              </Grid>
            )}
          </Grid>
        </Box>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
    </>
  );
};

export default DocumentsForm;
